import { FC, useEffect, useState } from 'react';

import './Cms.css';
import {
  AdminCategory,
  AdminItem,
  ApiError,
  CropBox,
  createItem,
  deleteCategory,
  deleteItem,
  resolveImageSrc,
  updateCategory,
  updateItem,
  uploadItemImage,
} from '../../api/client';
import { formatMoney } from '../../shared/functions/formatter';
import {
  Button,
  Card,
  CardTitle,
  ColorField,
  CurrencyField,
  DangerZone,
  Dialog,
  Field,
  FileTrigger,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Meta,
  Stack,
  StatusBadge,
  Switch,
} from '../../ui';
import type { Preview } from '../group/TillPreview';
import { trackSave } from './cmsActivity';
import {
  CategoryDraft,
  EMPTY_NEW_ITEM,
  ItemDraft,
  NewItemDraft,
  categoryDraftKey,
  clearDraft,
  isCategoryDraft,
  isNewItemDraft,
  itemDraftDirty,
  itemDraftKey,
  newItemDraftDirty,
  newItemDraftKey,
  parsePriceToCents,
  readDraft,
  restoreItemDrafts,
  toDraft,
  warnBeforeUnload,
  writeDraft,
} from './cmsDrafts';
import ImageCropper from './ImageCropper';

type PaletteSlot = 'palette1' | 'palette2' | 'palette3';

/**
 * The three stored palette slots, named for what the application does with them.
 *
 * palette2 paints the item cards' background and palette3 their text on the till
 * (see ItemSelector); palette1 is stored and returned by the API but nothing
 * renders it yet, so it says so rather than being given an invented role.
 */
const PALETTE_FIELDS: { slot: PaletteSlot; label: string; hint: string }[] = [
  { slot: 'palette1', label: 'Colour 1', hint: 'Stored, but not shown on the till yet.' },
  { slot: 'palette2', label: 'Card background', hint: 'Background of this category’s item cards.' },
  { slot: 'palette3', label: 'Card text', hint: 'Names and prices on those cards.' },
];

type CategoryCardProps = {
  category: AdminCategory;
  /** The items to show: everything, or just the search matches. */
  visibleItems: AdminItem[];
  /** 1-based position in the menu, shown as the order number. */
  position: number;
  total: number;
  /** True while this card is the one being dragged. */
  dragging: boolean;
  collapsed: boolean;
  /** True while the category's own fields are being edited. */
  editingCategory: boolean;
  /** The id of the item whose fields are being edited, if any. */
  editingItemId: number | null;
  /** True while this category's "new item" form is open. */
  addingItem: boolean;
  onToggleCollapsed: () => void;
  onEditCategory: (categoryId: number | null) => void;
  onEditItem: (itemId: number | null) => void;
  onAddingItem: (categoryId: number | null) => void;
  /** Move this category one slot earlier (-1) or later (+1). */
  onMove: (categoryId: number, delta: -1 | 1) => void;
  onDragStart: (categoryId: number) => void;
  onDragEnd: () => void;
  /** A dragged category was released over this one. */
  onDrop: (categoryId: number) => void;
  /** Account whose sessionStorage drafts these are; one account never sees another's. */
  draftScope: string;
  onCategoryChanged: (category: AdminCategory) => void;
  onCategoryRemoved: (categoryId: number) => void;
  onItemAdded: (categoryId: number, item: AdminItem) => void;
  onItemRemoved: (categoryId: number, itemId: number) => void;
  onNotice: (message: string) => void;
  onError: (message: string) => void;
  onUnauthorized: () => void;
  /** Publish the category while its fields are open, for the preview rail. */
  onPreview?: (preview: Preview | null) => void;
};

function itemsLabel(count: number): string {
  return count + (count === 1 ? ' item' : ' items');
}

const CategoryCard: FC<CategoryCardProps> = ({
  category,
  visibleItems,
  position,
  total,
  dragging,
  collapsed,
  editingCategory,
  editingItemId,
  addingItem,
  onToggleCollapsed,
  onEditCategory,
  onEditItem,
  onAddingItem,
  onMove,
  onDragStart,
  onDragEnd,
  onDrop,
  draftScope,
  onCategoryChanged,
  onCategoryRemoved,
  onItemAdded,
  onItemRemoved,
  onNotice,
  onError,
  onUnauthorized,
  onPreview,
}) => {
  // Restored from sessionStorage on the first render, so a step away and back
  // brings the operator's text with it.
  const [drafts, setDrafts] = useState<Record<number, ItemDraft>>(() => restoreItemDrafts(draftScope, category));
  // The category editor only mounts while it is open, so its stored draft needs
  // the header to advertise it once the operator navigates back. Derived, not
  // held in state: the chip must never outlive the draft it claims, and any
  // render with a fresh row (a reorder re-fetches the catalogue) has to be able
  // to withdraw it.
  const storedCategoryDraft = readDraft(categoryDraftKey(draftScope, category.id), isCategoryDraft);
  const hasCategoryDraft =
    storedCategoryDraft !== null &&
    (storedCategoryDraft.name.trim() !== category.name ||
      storedCategoryDraft.palette1 !== category.palette1 ||
      storedCategoryDraft.palette2 !== category.palette2 ||
      storedCategoryDraft.palette3 !== category.palette3);
  const [busyItemId, setBusyItemId] = useState<number | null>(null);
  const [cropTarget, setCropTarget] = useState<{ itemId: number; file: File } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState<{ kind: 'category' } | { kind: 'item'; item: AdminItem } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // A half-typed new item is a draft too: it is the same loss as an edit, one
  // form over.
  const [newItem, setNewItem] = useState<NewItemDraft>(
    () => readDraft(newItemDraftKey(draftScope, category.id), isNewItemDraft) ?? EMPTY_NEW_ITEM
  );

  /** Every failure funnels through here so an expired session is handled once. */
  const fail = (error: unknown, fallback: string) => {
    if (error instanceof ApiError && error.status === 401) {
      onUnauthorized();
      return;
    }
    onError(error instanceof Error ? error.message : fallback);
  };

  const draftFor = (item: AdminItem): ItemDraft => drafts[item.id] ?? toDraft(item);

  // Keep the stored copy in step: a dirty draft is preserved for a return trip, a
  // clean or absent one is removed, so a save never leaves a stale draft behind.
  useEffect(() => {
    for (const item of category.items) {
      const key = itemDraftKey(draftScope, category.id, item.id);
      const draft = drafts[item.id];
      if (draft && itemDraftDirty(item, draft)) writeDraft(key, draft);
      else clearDraft(key);
    }
  }, [drafts, category, draftScope]);

  const hasDirtyItem = category.items.some((item) => {
    const draft = drafts[item.id];
    return draft ? itemDraftDirty(item, draft) : false;
  });
  const hasNewItemDraft = newItemDraftDirty(newItem);

  // A create-form draft is stored exactly like an edit draft: kept while it holds
  // something, cleared once it is empty (added, or cancelled).
  useEffect(() => {
    const key = newItemDraftKey(draftScope, category.id);
    if (hasNewItemDraft) writeDraft(key, newItem);
    else clearDraft(key);
  }, [newItem, hasNewItemDraft, draftScope, category.id]);

  // Only a real reload or tab close warns; in-app navigation is never blocked.
  useEffect(() => warnBeforeUnload(hasDirtyItem || hasNewItemDraft), [hasDirtyItem, hasNewItemDraft]);

  const patchDraft = (itemId: number, patch: Partial<ItemDraft>, fallback: ItemDraft) => {
    setDrafts((current) => ({ ...current, [itemId]: { ...(current[itemId] ?? fallback), ...patch } }));
  };

  const dropDraft = (itemId: number) => {
    setDrafts((current) => {
      const next = { ...current };
      delete next[itemId];
      return next;
    });
  };

  const patchNewItem = (patch: Partial<NewItemDraft>) => {
    setNewItem((current) => ({ ...current, ...patch }));
  };

  const removeCategory = () => {
    trackSave(() => deleteCategory(category.id))
      .then(() => {
        onCategoryRemoved(category.id);
        onNotice('Category deleted.');
      })
      .catch((error: unknown) => fail(error, 'Could not delete the category.'))
      .finally(() => setConfirm(null));
  };

  const saveItem = (item: AdminItem) => {
    const draft = draftFor(item);
    const cents = parsePriceToCents(draft.price);
    if (!draft.title.trim()) {
      onError('Item title cannot be empty.');
      return;
    }
    if (cents === null) {
      onError('Price must be a number like 3.50.');
      return;
    }

    setBusyItemId(item.id);
    trackSave(() => updateItem(item.id, { title: draft.title.trim(), price_cents: cents, image_id: draft.imageId }))
      .then((updated) => {
        dropDraft(item.id);
        onCategoryChanged({ ...category, items: category.items.map((e) => (e.id === updated.id ? updated : e)) });
        onEditItem(null);
        onNotice('Item saved.');
      })
      .catch((error: unknown) => fail(error, 'Could not save the item.'))
      .finally(() => setBusyItemId(null));
  };

  const cancelItem = (item: AdminItem) => {
    dropDraft(item.id);
    onEditItem(null);
  };

  const removeItem = (item: AdminItem) => {
    setBusyItemId(item.id);
    trackSave(() => deleteItem(item.id))
      .then(() => {
        dropDraft(item.id);
        onItemRemoved(category.id, item.id);
        onNotice('Item deleted.');
      })
      .catch((error: unknown) => fail(error, 'Could not delete the item.'))
      .finally(() => {
        setBusyItemId(null);
        setConfirm(null);
      });
  };

  const setHidden = (item: AdminItem, hidden: boolean) => {
    const show = (next: boolean) =>
      onCategoryChanged({
        ...category,
        items: category.items.map((entry) => (entry.id === item.id ? { ...entry, is_hidden: next } : entry)),
      });

    // Flip straight away and undo it if the save fails: a till is often on a poor
    // connection, and a switch that waits for a round trip reads as broken.
    show(hidden);
    setBusyItemId(item.id);
    trackSave(() => updateItem(item.id, { is_hidden: hidden }))
      .then((updated) =>
        onCategoryChanged({
          ...category,
          items: category.items.map((entry) => (entry.id === updated.id ? updated : entry)),
        })
      )
      .catch((error: unknown) => {
        show(!hidden);
        fail(error, 'Could not change whether the item is on the till.');
      })
      .finally(() => setBusyItemId(null));
  };

  const addItem = () => {
    const cents = parsePriceToCents(newItem.price);
    if (!newItem.title.trim() || cents === null) {
      onError('Give the new item a title and a price like 3.50.');
      return;
    }
    trackSave(() =>
      createItem({
        category_id: category.id,
        title: newItem.title.trim(),
        price_cents: cents,
        is_hidden: newItem.hidden,
      })
    )
      .then((created) => {
        setNewItem(EMPTY_NEW_ITEM);
        onItemAdded(category.id, created);
        onAddingItem(null);
        onNotice('Item added.');
      })
      .catch((error: unknown) => fail(error, 'Could not add the item.'));
  };

  /** Upload the original file with the chosen crop; the server makes it square. */
  const applyCrop = (crop: CropBox) => {
    if (!cropTarget) return;
    const { itemId, file } = cropTarget;
    const item = category.items.find((entry) => entry.id === itemId);
    setUploading(true);

    trackSave(() => uploadItemImage(file, crop))
      .then((uploaded) => {
        if (item) {
          patchDraft(itemId, { imageId: uploaded.id, imageUrl: uploaded.url }, toDraft(item));
        }
        setCropTarget(null);
      })
      .catch((error: unknown) => fail(error, 'Could not upload the image.'))
      .finally(() => setUploading(false));
  };

  const menuClick = (action: () => void) => {
    setMenuOpen(false);
    action();
  };

  const confirmTitle =
    confirm?.kind === 'category' ? 'Delete ' + category.name + '?' : confirm?.kind === 'item' ? 'Delete ' + confirm.item.title + '?' : '';
  const confirmDescription =
    confirm?.kind === 'category'
      ? 'The category and its ' +
        itemsLabel(category.items.length) +
        ' are removed from the catalogue. Items already sold keep their own copy of the name and price.'
      : 'It is removed from the catalogue and stops appearing on the till. Sales already recorded keep their own copy of the name and price.';

  return (
    <Card
      className={dragging ? 'cmsCategory cmsCategoryDragging' : 'cmsCategory'}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(category.id);
      }}
    >
      <header className="cmsCategoryHeader">
        <IconButton
          className="cmsCollapse"
          size="sm"
          label={(collapsed ? 'Expand ' : 'Collapse ') + category.name}
          aria-expanded={!collapsed}
          aria-controls={'cmsItems-' + category.id}
          onClick={onToggleCollapsed}
        >
          {/* An inline SVG, like every other icon in the app: a text glyph sat on
              the font baseline, so its metrics decided where it landed inside the
              button. The path follows SelectedItems' chevron language. */}
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d={collapsed ? 'M10 7l5 5-5 5z' : 'M7 10l5 5 5-5z'} />
          </svg>
        </IconButton>
        <span
          className="cmsDragHandle"
          draggable
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = 'move';
            // Some browsers refuse to begin a drag unless data is attached.
            event.dataTransfer.setData('text/plain', String(category.id));
            onDragStart(category.id);
          }}
          onDragEnd={onDragEnd}
          aria-hidden="true"
          title="Drag to reorder"
        >
          ⠿
        </span>
        <span className="cmsCategoryOrder" title={'Menu position ' + position}>
          {position}
        </span>

        {editingCategory ? (
          <CategoryEditor
            key={'cat-' + category.id}
            category={category}

            draftScope={draftScope}
            onSaved={(updated) => {
              onCategoryChanged(updated);
              onEditCategory(null);
              onNotice('Category saved.');
            }}
            onCancel={() => onEditCategory(null)}
            onError={onError}
            onUnauthorized={onUnauthorized}
            onPreview={onPreview}
          />
        ) : (
          <div className="cmsCategoryTitleBlock">
            <CardTitle level={3} className="cmsCategoryTitle">
              {category.name}
            </CardTitle>
            {hasCategoryDraft && (
              <StatusBadge className="cmsDraftBadge" tone="warning">
                unsaved
              </StatusBadge>
            )}
            <Meta>{itemsLabel(category.items.length)}</Meta>
            <span className="cmsPalettePreview" aria-label="Category colours">
              {PALETTE_FIELDS.map((field) => (
                <span
                  key={field.slot}
                  className="cmsSwatch"
                  style={{ backgroundColor: category[field.slot] || '#000000' }}
                  title={field.label + ' — ' + field.hint}
                />
              ))}
            </span>
          </div>
        )}

        <Menu className="cmsCategoryMenuWrap" open={menuOpen} onOpenChange={setMenuOpen} label={'Actions for ' + category.name}>
          <MenuItem onClick={() => menuClick(() => onEditCategory(category.id))}>Edit category</MenuItem>
          <MenuItem disabled={position === 1} onClick={() => menuClick(() => onMove(category.id, -1))}>
            Move up
          </MenuItem>
          <MenuItem disabled={position === total} onClick={() => menuClick(() => onMove(category.id, 1))}>
            Move down
          </MenuItem>
          <MenuItem danger onClick={() => menuClick(() => setConfirm({ kind: 'category' }))}>
            Delete category
          </MenuItem>
        </Menu>
      </header>

      {!collapsed && (
        <div id={'cmsItems-' + category.id}>
          <ul className="cmsItems">
            {visibleItems.map((item) => {
              const draft = draftFor(item);
              const editing = editingItemId === item.id;
              const dirty = itemDraftDirty(item, draft);
              const cents = parsePriceToCents(draft.price);
              const preview = draft.imageUrl ?? item.image_url ?? item.img;
              const busy = busyItemId === item.id;

              if (editing) {
                return (
                  <li key={item.id} className="cmsItem cmsItemEditing">
                    <Stack gap={5} className="cmsItemEdit">
                      {dirty && <StatusBadge tone="warning">unsaved</StatusBadge>}
                      <div className="cmsImageEditor">
                        <span className="cmsThumb">
                          {preview ? <img src={resolveImageSrc(preview)} alt="" /> : <span className="cmsThumbEmpty">no image</span>}
                        </span>
                        <FileTrigger
                          accept="image/*"
                          onFile={(file) => setCropTarget({ itemId: item.id, file })}
                          variant="secondary"
                          size="sm"
                        >
                          {preview ? 'Change image' : 'Add image'}
                        </FileTrigger>
                        {item.image_id !== null && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => patchDraft(item.id, { imageId: null, imageUrl: null }, toDraft(item))}
                          >
                            Remove image
                          </Button>
                        )}
                      </div>
                      <div className="cmsFields">
                        <Field label="Item title">
                          <Input
                            value={draft.title}
                            aria-label={'Title for ' + item.title}
                            onChange={(event) => patchDraft(item.id, { title: event.target.value }, toDraft(item))}
                          />
                        </Field>
                        <CurrencyField
                          label="Price"
                          aria-label={'Price for ' + item.title}
                          value={draft.price}
                          onChange={(event) => patchDraft(item.id, { price: event.target.value }, toDraft(item))}
                        />
                        <Field label="Visibility">
                          <Switch
                            className="cmsSwitchState"
                            checked={!item.is_hidden}
                            disabled={busy}
                            label={item.is_hidden ? 'Hidden' : 'Shown'}
                            ariaLabel={'Show ' + item.title + ' on the till'}
                            onChange={(checked) => setHidden(item, !checked)}
                          />
                        </Field>
                      </div>
                      <Stack direction="row" gap={3} className="cmsDraftActions">
                        <Button variant="primary" disabled={!dirty || busy || cents === null} onClick={() => saveItem(item)}>
                          {busy ? '…' : 'Save changes'}
                        </Button>
                        {dirty && (
                          <Button variant="ghost" onClick={() => dropDraft(item.id)}>
                            Discard
                          </Button>
                        )}
                        <Button variant="secondary" onClick={() => cancelItem(item)}>
                          Cancel
                        </Button>
                      </Stack>
                      <DangerZone
                        title="Delete item"
                        description="Removes it from the catalogue. Recorded sales keep their own copy of the name and price."
                        action={
                          <Button variant="destructive" disabled={busy} onClick={() => setConfirm({ kind: 'item', item })}>
                            Delete item
                          </Button>
                        }
                      />
                    </Stack>
                  </li>
                );
              }

              return (
                <li key={item.id} className={item.is_hidden ? 'cmsItem cmsItemHidden' : 'cmsItem'}>
                  <span className="cmsThumb">
                    {preview ? <img src={resolveImageSrc(preview)} alt="" /> : <span className="cmsThumbEmpty">no image</span>}
                  </span>
                  <span className="cmsItemName" title={item.title}>
                    {item.title}
                  </span>
                  <span className="cmsItemMeta">
                    {dirty && <StatusBadge tone="warning">unsaved</StatusBadge>}
                    <span className="cmsItemPriceRead">{formatMoney(item.price_cents)}</span>
                    <StatusBadge className="cmsStatus" tone={item.is_hidden ? 'neutral' : 'success'}>
                      {item.is_hidden ? 'Hidden' : 'Shown'}
                    </StatusBadge>
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="cmsItemEditButton"
                    aria-label={'Edit ' + item.title}
                    onClick={() => onEditItem(item.id)}
                  >
                    Edit
                  </Button>
                </li>
              );
            })}
          </ul>

          {visibleItems.length === 0 && (
            <Meta className="cmsItemsEmpty">
              {category.items.length === 0 ? 'No items in this category yet.' : 'No items match your search.'}
            </Meta>
          )}

          <div className="cmsAddItemFooter">
            {addingItem ? (
              <Stack gap={4} className="cmsAddItemForm">
                <CardTitle level={4}>New item</CardTitle>
                <Field label="Title">
                  <Input
                    value={newItem.title}
                    autoFocus
                    aria-label="New item title"
                    onChange={(event) => patchNewItem({ title: event.target.value })}
                  />
                </Field>
                <CurrencyField
                  label="Price"
                  aria-label="New item price"
                  value={newItem.price}
                  onChange={(event) => patchNewItem({ price: event.target.value })}
                />
                <Field label="Visibility">
                  <Switch
                    className="cmsSwitchState"
                    checked={!newItem.hidden}
                    label={newItem.hidden ? 'Hidden' : 'Shown'}
                    ariaLabel="Show the new item on the till"
                    onChange={(checked) => patchNewItem({ hidden: !checked })}
                  />
                </Field>
                <Stack direction="row" gap={3}>
                  <Button variant="primary" onClick={addItem}>
                    Add item
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      // Cancel is a deliberate throw-away, so the stored draft
                      // goes with it - unlike closing the view, which keeps it.
                      setNewItem(EMPTY_NEW_ITEM);
                      onAddingItem(null);
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack direction="row" gap={3} align="center">
                <Button variant="ghost" onClick={() => onAddingItem(category.id)}>
                  + Add item
                </Button>
                {hasNewItemDraft && <StatusBadge tone="warning">unsaved</StatusBadge>}
              </Stack>
            )}
          </div>
        </div>
      )}

      {cropTarget && <ImageCropper file={cropTarget.file} busy={uploading} onCancel={() => setCropTarget(null)} onApply={applyCrop} />}

      <Dialog
        open={confirm !== null}
        onOpenChange={(next) => {
          if (!next) setConfirm(null);
        }}
        title={confirmTitle}
        description={confirmDescription}
        tone="danger"
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busyItemId !== null}
              onClick={() => {
                if (confirm?.kind === 'category') removeCategory();
                else if (confirm?.kind === 'item') removeItem(confirm.item);
              }}
            >
              {confirm?.kind === 'category' ? 'Delete category' : 'Delete item'}
            </Button>
          </>
        }
      />
    </Card>
  );
};

type CategoryEditorProps = {
  category: AdminCategory;
  /** Account whose sessionStorage draft this is. */
  draftScope: string;
  onSaved: (category: AdminCategory) => void;
  onCancel: () => void;
  onError: (message: string) => void;
  onUnauthorized: () => void;
  /** Publish the live palette so the page can preview the till cards. */
  onPreview?: (preview: Preview | null) => void;
};

/**
 * The category's own fields, mounted only while it is being edited.
 *
 * Living in a keyed child means it starts from the category the server sent and
 * is thrown away on Cancel, with no state to keep in step by hand.
 */
const CategoryEditor: FC<CategoryEditorProps> = ({ category, draftScope, onSaved, onCancel, onError, onUnauthorized, onPreview }) => {
  const [draft, setDraft] = useState<CategoryDraft>(
    () =>
      readDraft(categoryDraftKey(draftScope, category.id), isCategoryDraft) ?? {
        name: category.name,
        palette1: category.palette1,
        palette2: category.palette2,
        palette3: category.palette3,
      }
  );
  const [busy, setBusy] = useState(false);

  const name = draft.name.trim();
  const paletteChanged =
    draft.palette1 !== category.palette1 || draft.palette2 !== category.palette2 || draft.palette3 !== category.palette3;
  const dirty = name !== category.name || paletteChanged;

  // Persist while the fields differ from the row, clear as soon as they do not.
  useEffect(() => {
    const key = categoryDraftKey(draftScope, category.id);
    if (dirty) writeDraft(key, draft);
    else clearDraft(key);
  }, [draft, dirty, draftScope, category.id]);

  // Only a real reload or tab close warns; closing the editor is a Cancel.
  useEffect(() => warnBeforeUnload(dirty), [dirty]);

  /** Throw the restored draft away and show the saved row, keeping the fields open. */
  const discard = () => {
    clearDraft(categoryDraftKey(draftScope, category.id));
    setDraft({
      name: category.name,
      palette1: category.palette1,
      palette2: category.palette2,
      palette3: category.palette3,
    });
  };

  const cancel = () => {
    clearDraft(categoryDraftKey(draftScope, category.id));
    onCancel();
  };

  // Live, not on save: the point of the preview is watching a colour land on the
  // cards, and it is withdrawn when the fields close.
  useEffect(() => {
    onPreview?.({
      kind: 'category',
      name: name || category.name,
      palette2: draft.palette2,
      palette3: draft.palette3,
      items: category.items.map((item) => ({
        id: item.id,
        title: item.title,
        priceCents: item.price_cents,
        img: item.image_url ?? item.img,
      })),
    });
    return () => onPreview?.(null);
  }, [name, draft, category, onPreview]);

  const save = () => {
    if (!name) {
      onError('Category name cannot be empty.');
      return;
    }
    setBusy(true);
    trackSave(() =>
      updateCategory(category.id, {
        ...(name !== category.name ? { name } : {}),
        ...(paletteChanged ? { palette1: draft.palette1, palette2: draft.palette2, palette3: draft.palette3 } : {}),
      })
    )
      .then((updated) => {
        // The row now matches, so there is nothing left to restore.
        clearDraft(categoryDraftKey(draftScope, category.id));
        onSaved(updated);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 401) {
          onUnauthorized();
          return;
        }
        onError(error instanceof Error ? error.message : 'Could not save the category.');
      })
      .finally(() => setBusy(false));
  };

  return (
    <Stack gap={4} className="cmsCategoryEditor">
      {dirty && <StatusBadge tone="warning">unsaved</StatusBadge>}
      <Field label="Category name">
        <Input
          value={draft.name}
          autoFocus
          aria-label={'Category name for ' + category.name}
          onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') save();
            if (event.key === 'Escape') cancel();
          }}
        />
      </Field>
      <div className="cmsPaletteFields">
        {PALETTE_FIELDS.map((field) => (
          <ColorField
            key={field.slot}
            label={field.label}
            value={draft[field.slot] || '#000000'}
            description={field.hint}
            onChange={(value) => setDraft((current) => ({ ...current, [field.slot]: value }))}
          />
        ))}
      </div>
      <Stack direction="row" gap={3} className="cmsDraftActions">
        <Button variant="primary" disabled={!dirty || busy} onClick={save}>
          Save changes
        </Button>
        {dirty && (
          <Button variant="ghost" onClick={discard}>
            Discard
          </Button>
        )}
        <Button variant="secondary" onClick={cancel}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default CategoryCard;
